import type { SkillManifest, TestRun } from "@ai-sales/contracts";

export type ReleaseEvidenceStatus = "current_passed" | "current_failed" | "stale" | "legacy_passed" | "none";

export interface ReleaseEvidenceAssessment {
  status: ReleaseEvidenceStatus;
  run?: TestRun;
}

function isCompletePassingRun(run: TestRun): boolean {
  return run.status === "passed" && run.results.length > 0 && run.failed === 0 &&
    run.passed === run.results.length && run.results.every(({ status }) => status === "passed");
}

/**
 * Runs are supplied newest-first by the Runtime API. The UI mirrors the server
 * release gate but remains informational; the API is the security boundary.
 */
export function assessReleaseEvidence(skill: SkillManifest, runs: TestRun[]): ReleaseEvidenceAssessment {
  const versionRuns = runs.filter((run) => run.skillKey === skill.key && run.skillVersion === skill.version);
  const suite = skill.modules?.testSuite;
  if (!suite?.datasetId) {
    const latestLegacyRun = versionRuns[0];
    if (!latestLegacyRun) return { status: "none" };
    return isCompletePassingRun(latestLegacyRun)
      ? { status: "legacy_passed", run: latestLegacyRun }
      : { status: "current_failed", run: latestLegacyRun };
  }
  const latestCurrentRun = versionRuns.find((run) =>
    run.testDatasetId === suite.datasetId && run.testDatasetVersion === suite.datasetVersion
  );
  if (latestCurrentRun) {
    return isCompletePassingRun(latestCurrentRun)
      ? { status: "current_passed", run: latestCurrentRun }
      : { status: "current_failed", run: latestCurrentRun };
  }
  return versionRuns[0] ? { status: "stale", run: versionRuns[0] } : { status: "none" };
}
